import { useEffect, useState } from "react";
import { useReportSchedule } from "@/hooks/useReportSchedule";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { TimePicker } from "@/components/ui/TimePicker";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Mail, Send, Loader2, Calendar, Clock } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

interface ScheduleReportDialogProps {
  trigger?: React.ReactNode;
  currentSchedule?: string;
  currentTime?: string;
}

export const ScheduleReportDialog = ({ trigger, currentSchedule, currentTime }: ScheduleReportDialogProps) => {
  const [open, setOpen] = useState(false);
  const { config, loading, saving, save, isScheduled } = useReportSchedule();
  const [reportType, setReportType] = useState("analytics");
  const [frequency, setFrequency] = useState(currentSchedule || "weekly");
  const [deliveryTime, setDeliveryTime] = useState(currentTime || "08:00");
  const [recipientEmail, setRecipientEmail] = useState("");
  const [isSending, setIsSending] = useState(false);

  useEffect(() => {
    if (loading) return;
    setReportType(config.report_type);
    setFrequency(currentSchedule || config.schedule);
    setDeliveryTime(currentTime || config.time);
    setRecipientEmail(config.recipient);
  }, [loading, config, currentSchedule, currentTime]);

  const reportTypes = [
    { value: "analytics", label: "Analytics Summary", description: "Key metrics and KPI overview" },
    { value: "workflow", label: "Workflow Report", description: "Pipeline execution results" },
    { value: "predictive", label: "Predictive Model Report", description: "Forecasts and model performance" },
  ];

  const handleSendTest = async () => {
    setIsSending(true);
    try {
      const selectedReport = reportTypes.find(r => r.value === reportType);
      const { data, error } = await supabase.functions.invoke("send-test-report", {
        body: {
          reportLabel: selectedReport?.label || "Report",
          origin: window.location.origin,
        },
      });

      if (error) throw error;
      if (data && (data as any).success === false) {
        throw new Error((data as any).reason || "Email could not be sent");
      }
      toast.success(`Test report sent to ${(data as any)?.recipient ?? "your email"}`);
    } catch (err: any) {
      toast.error(`Failed to send test report: ${err.message}`);
    } finally {
      setIsSending(false);
    }
  };

  const handleSaveSchedule = async () => {
    if (!recipientEmail) {
      toast.error("Please enter a recipient email");
      return;
    }
    try {
      await save({
        schedule: frequency,
        time: deliveryTime,
        report_type: reportType,
        recipient: recipientEmail,
        email_notifications: true,
      });
      toast.success(`Report schedule saved: ${frequency} at ${deliveryTime}`);
      setOpen(false);
    } catch (err: any) {
      toast.error(err?.message || "Could not save your report schedule");
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button variant="outline" size="sm" className="gap-2">
            <Mail className="h-4 w-4" />
            Configure Report Emails
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5 text-primary" />
            Schedule Report Emails
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-5 py-2">
          {/* Report Type */}
          <div className="space-y-2">
            <Label>Report Type</Label>
            <Select value={reportType} onValueChange={setReportType}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {reportTypes.map(rt => (
                  <SelectItem key={rt.value} value={rt.value}>
                    <div className="flex flex-col">
                      <span>{rt.label}</span>
                      <span className="text-xs text-muted-foreground">{rt.description}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Frequency & Time */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="flex items-center gap-1.5">
                <Calendar className="h-3.5 w-3.5" />
                Frequency
              </Label>
              <Select value={frequency} onValueChange={setFrequency}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="daily">Daily</SelectItem>
                  <SelectItem value="weekly">Weekly</SelectItem>
                  <SelectItem value="monthly">Monthly</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="flex items-center gap-1.5">
                <Clock className="h-3.5 w-3.5" />
                Delivery Time
              </Label>
              <TimePicker value={deliveryTime} onChange={setDeliveryTime} />
            </div>
          </div>

          {/* Recipient */}
          <div className="space-y-2">
            <Label>Recipient Email</Label>
            <Input
              type="email"
              placeholder="you@company.com"
              value={recipientEmail}
              onChange={e => setRecipientEmail(e.target.value)}
            />
          </div>

          {/* Current Config Summary */}
          <Card className="bg-muted/50">
            <CardContent className="p-3 flex items-center gap-3">
              <Badge variant="secondary" className="capitalize">{frequency}</Badge>
              <span className="text-sm text-muted-foreground">at {deliveryTime}</span>
              <Badge variant="outline" className="ml-auto">
                {reportTypes.find(r => r.value === reportType)?.label}
              </Badge>
              <Badge variant={isScheduled ? "default" : "secondary"}>
                {isScheduled ? "Active" : "Not scheduled yet"}
              </Badge>
            </CardContent>
          </Card>

          {/* Actions */}
          <div className="flex gap-3">
            <Button variant="outline" className="flex-1 gap-2" onClick={handleSendTest} disabled={isSending}>
              {isSending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              Send Test
            </Button>
            <Button className="flex-1 gap-2" onClick={handleSaveSchedule} disabled={saving || loading}>
              {saving && <Loader2 className="h-4 w-4 animate-spin" />}
              Save Schedule
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
